import dayjs from 'dayjs';
import { formatToNaira } from '.';
import {
  IpoInvestment,
  IpoInvestor,
  IpoOfferGroup,
  IpoOfferSubscriptionsPage,
  IpoPublicOfferSubscription,
  IpoSubscription,
  IpoSubscriptionListPagination,
  IpoUserInvestmentsPage,
} from '../../types';

const INVESTIFY_IMAGE_BASE =
  process.env.REACT_APP_INVESTIFY_IMAGE_BASE ||
  'https://storage.googleapis.com/piggybankservice.appspot.com/investmentimgfiles/thumb.';

export const getInvestifyImageUrl = (image?: string) => {
  if (!image) return '';
  if (image.startsWith('http')) return image;
  return `${INVESTIFY_IMAGE_BASE}${image}`;
};

export const formatIpoInvestmentCard = (item: IpoInvestment) => ({
  ...item,
  investId: item.offer_id,
  title: item.investment_title,
  image: getInvestifyImageUrl(item.investment_image),
  keyPoints: [
    {
      label: 'Total Amount',
      value: formatToNaira(Number(item.total_amount_purchased)),
    },
    {
      label: 'Units',
      value: Number(item.total_units_purchased).toLocaleString(),
    },
  ],
  returnsInfo: {
    percent: `${item.subscription_count}`,
    returnsText: item.subscription_count === 1 ? 'subscription' : 'subscriptions',
    percentStyle: { color: '#6624CE' },
  },
});

export const formatInvestifyStatus = (status: string) =>
  status
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const toMilliseconds = (value?: number | string | null): number | undefined => {
  if (value == null || value === '') return undefined;
  if (typeof value === 'string' && Number.isNaN(Number(value))) {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return undefined;
  return numeric < 1e12 ? numeric * 1000 : numeric;
};

export const getIpoSubscriptionBrokerName = (subscription: IpoSubscription) =>
  subscription.meta?.data?.subscription?.brokerName || subscription.broker_name || 'N/A';

export const getIpoSubscriptionOfferName = (subscription: IpoSubscription) =>
  subscription.offer_name || subscription.meta?.data?.subscription?.offerName || '';

const IPO_STATUS_TAG_CLASS: Record<string, string> = {
  PENDING: 'Pending',
  SUBMITTED: 'Submitted',
  PROCESSING: 'Pending',
  ALLOTTED: 'Success',
  SETTLED: 'Success',
  UNDER_ALLOTTED: 'On-Hold',
  OVER_SUBSCRIBED: 'On-Hold',
  FAILED: 'Failed',
  REVERSED: 'Reversed',
  UNKNOWN: 'Reversed',
  SUCCESS: 'Success',
  ACTIVE: 'Success',
};

export const getIpoApiStatusTag = (status?: string) => {
  const key = (status || 'UNKNOWN').toUpperCase();

  return {
    label: formatInvestifyStatus(key),
    tagClass: IPO_STATUS_TAG_CLASS[key] || 'Reversed',
  };
};

export const getIpoSubscriptionStatusTag = (subscription: Pick<IpoSubscription, 'status'> | { status?: string }) =>
  getIpoApiStatusTag(subscription.status);

export const getIpoUnitPrice = (amount?: string | number | null, units?: string | number | null) => {
  const total = Number(amount);
  const quantity = Number(units);
  if (!total || !quantity) return 0;
  return total / quantity;
};

export const trimIpoIdentifier = (value?: string | null) => value?.trim() || '';

export const mapIpoOfferSubscription = (row: Record<string, any> = {}): IpoPublicOfferSubscription => ({
  id: Number(row.id),
  offerId: String(row.offerId || row.offer_id || ''),
  offerName: row.offerName || row.offer_name || '',
  numberOfUnits: String(row.numberOfUnits ?? row.total_shares ?? ''),
  subscriptionAmount: String(row.subscriptionAmount ?? row.total_amount ?? ''),
  submissionDate: Number(row.submissionDate ?? row.created_at ?? 0),
  status: row.status || 'UNKNOWN',
  isProcessing: Boolean(row.isProcessing),
  openingDate: row.openingDate ?? null,
  chn: row.chn ?? null,
  chnStatus: row.chnStatus || '',
  cscs: row.cscs ?? null,
  cscsStatus: row.cscsStatus || '',
  providerSubscriptionId: row.providerSubscriptionId ?? row.provider_subscription_id ?? null,
  provider: row.provider || '',
  userId: row.userId || row.user_id || '',
  rawStatus: row.rawStatus ?? row.raw_status ?? null,
  lastError: row.lastError ?? row.last_error ?? null,
  needsReconciliation: Boolean(row.needsReconciliation),
});

export const parseIpoOfferSubscriptions = (
  payload: unknown,
  page: number,
  limit: number,
): IpoOfferSubscriptionsPage => {
  const root = (payload && typeof payload === 'object' ? payload : {}) as Record<string, any>;
  const data = root.data && typeof root.data === 'object' ? root.data : root;
  const rows = data.subscriptions ?? [];
  const list = (Array.isArray(rows) ? rows : []).map(mapIpoOfferSubscription);
  const pagination = data.pagination || {
    page,
    limit,
    total: list.length < limit ? (page - 1) * limit + list.length : 0,
  };

  return {
    subscriptions: list,
    pagination: {
      page: Number(pagination.page || page),
      limit: Number(pagination.limit || limit),
      total: Number(pagination.total || 0),
    },
    hasNextPage: pagination.total ? page * limit < Number(pagination.total) : list.length >= limit,
  };
};

export const mapIpoUserInvestmentToOffer = (item: IpoInvestment): IpoOfferGroup => ({
  offerId: item.offer_id || item.investment_offer_id,
  offerName: item.investment_title || 'Public Offer',
  subscriptionCount: Number(item.subscription_count || 0),
  totalAmount: Number(item.total_amount_purchased || 0),
  totalUnits: Number(item.total_units_purchased || 0),
  image: getInvestifyImageUrl(item.investment_image),
});

export const parseIpoUserInvestments = (payload: unknown): IpoUserInvestmentsPage => {
  const root = (payload && typeof payload === 'object' ? payload : {}) as Record<string, any>;
  const data = root.data && typeof root.data === 'object' ? root.data : root;
  const bundle = data.investments && typeof data.investments === 'object' ? data.investments : data;
  const rows = bundle?.data?.subscriptions ?? bundle?.subscriptions ?? [];
  const list = Array.isArray(rows) ? rows : [];
  const pageInfo = bundle?.pageInfo ?? {};
  const last = list[list.length - 1];

  return {
    investments: list.map(mapIpoUserInvestmentToOffer),
    hasNextPage: Boolean(pageInfo.hasNextPage),
    endCursor: pageInfo.endCursor ?? pageInfo.cursor ?? last?.last_subscription_id ?? null,
  };
};

export const DEFAULT_IPO_PAGE_SIZE = 20;
export const IPO_OFFER_LOOKUP_PAGE_SIZE = 20;
export const IPO_OFFER_LOOKUP_MAX_PAGES = 25;

export const defaultIpoPagination = (page: number, limit: number): IpoSubscriptionListPagination => ({
  page,
  limit,
  total: 0,
});

export const formatIpoDate = (value?: number | string | null, withTime = false) => {
  const ms = toMilliseconds(value);
  if (!ms) return withTime ? null : '—';
  return dayjs(ms).format(withTime ? 'D MMM YYYY | h:mm A' : 'D MMM YYYY');
};

export const parseIpoInvestor = (payload: unknown): IpoInvestor | null => {
  if (!payload || typeof payload !== 'object') return null;

  const root = payload as Record<string, unknown>;
  const data = (root.data && typeof root.data === 'object' ? root.data : root) as Record<string, unknown>;
  const investor = (data.investor && typeof data.investor === 'object' ? data.investor : data) as IpoInvestor;

  if (!investor || (investor.chn == null && investor.cscs == null && investor.userId == null && investor.id == null)) {
    return null;
  }

  return {
    ...investor,
    chn: trimIpoIdentifier(investor.chn) || null,
    cscs: trimIpoIdentifier(investor.cscs) || null,
  };
};

export const mapIpoSubscriptionDetailPayload = (payload: unknown) => {
  const data = payload && typeof payload === 'object' ? (payload as Record<string, any>) : {};
  return mapIpoOfferSubscription((data.subscription ?? data) || {});
};

export const findIpoOfferById = (items: IpoOfferGroup[] = [], offerId?: string | null) =>
  items.find((item) => item.offerId === offerId) || null;

export const lookupIpoOfferSummary = async (
  offerId: string,
  fetchPage: (cursor: string | number | null) => Promise<IpoUserInvestmentsPage>,
  maxPages = IPO_OFFER_LOOKUP_MAX_PAGES,
) => {
  let cursor: string | number | null = null;

  for (let index = 0; index < maxPages; index += 1) {
    const page = await fetchPage(cursor);
    const match = findIpoOfferById(page.investments, offerId);
    if (match) return match;
    if (!page.hasNextPage || page.endCursor == null) return null;
    cursor = page.endCursor;
  }

  return null;
};
